/**
 * useAvailableModels — BYOK-strict hook for discovering the tenant's LLM models.
 *
 * Fetches GET /api/llm/models on mount. Returns:
 *   { models: {openai: [...], anthropic: [...], ...}, loading, error, llmConfigured, refresh }
 *
 * - No hardcoded fallback list. If the tenant has no provider configured, the
 *   endpoint returns HTTP 412 and this hook surfaces `llmConfigured = false`.
 * - Dropdowns that consume this hook render an empty state (<LLMNotConfigured/>)
 *   when llmConfigured is false.
 * - Models list comes from the provider SDK at request time, so newly-released
 *   models appear in the dropdown without a code change.
 */

import { useState, useEffect, useCallback } from "react";
import { llmApi } from "../api/client";

export function useAvailableModels() {
  const [models, setModels] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [llmConfigured, setLlmConfigured] = useState(null); // null = unknown

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await llmApi.models();
      setModels(resp?.data || {});
      setLlmConfigured(true);
    } catch (err) {
      if (err?.status === 412) {
        setModels({});
        setLlmConfigured(false);
        setError(null);
      } else {
        setError(err?.message || "Failed to load models");
        setLlmConfigured(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return { models, loading, error, llmConfigured, refresh };
}


/**
 * useProvidersConfigured — lightweight readiness check for the onboarding gate.
 *
 * Returns { configured: [...], llmReady: bool, loading, error, refresh }.
 */
export function useProvidersConfigured() {
  const [configured, setConfigured] = useState([]);
  const [llmReady, setLlmReady] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await llmApi.providersConfigured();
      setConfigured(resp?.data?.configured || []);
      setLlmReady(!!resp?.data?.llm_ready);
    } catch (err) {
      setError(err?.message || "Failed to check LLM configuration");
      setLlmReady(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return { configured, llmReady, loading, error, refresh };
}
